import './TransactionFilter.scss';
import {useCallback, useEffect, useMemo, useState} from 'react';
import DescriptionSearchFilter from './DescriptionSearchFilter';
import DateRangeFilter from './DateRangeFilter';
import AccountsFilter from './AccountsFilter';
import TransactionTypesFilter from './TransactionTypesFilter';
import ProjectsFilter from './ProjectsFilter';
import TimelinePresets from '../TimelinePresets';
import {computeRange, availableYears} from '../../../pages/TimelinePage/timelineUtils';

const TransactionFilter = ({accounts = [], projects = [], onFiltersChange, initialValues = {}, initialPreset = 'last-year', enabled = {}, expanded = false, onExpandedChange}) => {
    const show = {searchText: true, dateRange: true, accounts: true, types: true, projects: true, ...enabled};
    const [preset, setPreset] = useState(initialPreset);

    const years = useMemo(() => availableYears(accounts), [accounts]);

    const [filter, setFilter] = useState(() => {
        const {from, to} = computeRange('last-year');
        return {
            searchText: '',
            from,
            to,
            accountIds: null,
            types: null,
            projectIds: null,
            ...initialValues,
        };
    });

    useEffect(() => {
        onFiltersChange(filter);
    }, [filter, onFiltersChange]);

    const patch = (partial) => {
        setFilter(prev => {
            const next = {...prev, ...partial};
            if (Array.isArray(next.accountIds) && next.accountIds.length === 0) next.accountIds = null;
            if (Array.isArray(next.types) && next.types.length === 0) next.types = null;
            if (Array.isArray(next.projectIds) && next.projectIds.length === 0) next.projectIds = null;
            return next;
        });
    };

    const updateText = useCallback(v => patch({searchText: v}), []); // eslint-disable-line

    const handlePresetChange = (newPreset) => {
        setPreset(newPreset);
        const {from, to} = computeRange(newPreset);
        patch({from, to});
    };

    const handleDateChange = ({from, to}) => {
        setPreset(null);
        patch({from, to});
    };

    return (
        <>
            <div
                className={`tx-filter__backdrop${expanded ? ' is-open' : ''}`}
                onClick={() => onExpandedChange?.(false)}
                aria-hidden="true"
            />
            <aside
                className={`tx-filter tx-filter--${expanded ? 'open' : 'closed'}`}
                aria-label="Filters"
            >
                <div className={`tx-filter__content${expanded ? ' is-open' : ' is-closed'}`}>
                    {show.searchText && (
                        <DescriptionSearchFilter
                            value={filter.searchText}
                            onChange={updateText}
                        />
                    )}
                    {show.dateRange && (
                        <>
                            <section className="filter-card">
                                <div className="filter-card__header">
                                    <div className="filter-card__title">Period</div>
                                </div>
                                <TimelinePresets
                                    preset={preset ?? ''}
                                    years={years}
                                    onChange={handlePresetChange}
                                />
                            </section>
                            <DateRangeFilter
                                from={filter.from}
                                to={filter.to}
                                onChange={handleDateChange}
                            />
                        </>
                    )}
                    {show.accounts && (
                        <AccountsFilter
                            accounts={accounts}
                            selectedIds={filter.accountIds ?? []}
                            onChange={ids => patch({accountIds: ids})}
                        />
                    )}
                    {show.types && (
                        <TransactionTypesFilter
                            selectedTypes={filter.types ?? []}
                            onChange={types => patch({types})}
                        />
                    )}
                    {show.projects && (!filter.types || filter.types.includes('INCOME')) && (
                        <ProjectsFilter
                            projects={projects}
                            selectedIds={filter.projectIds ?? []}
                            onChange={projectIds => patch({projectIds})}
                        />
                    )}
                </div>
            </aside>
        </>
    );
};

export default TransactionFilter;